import React, { useCallback, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Link,
  Stack,
  Tooltip,
  Typography,
  alpha,
} from "@mui/material";
import AccountBalanceIcon from "@mui/icons-material/AccountBalance";
import SyncIcon from "@mui/icons-material/Sync";
import GavelIcon from "@mui/icons-material/Gavel";
import CloseIcon from "@mui/icons-material/Close";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import { Link as RouterLink } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { resolveTenantDatabaseName } from "../../../utils/resolveTenantDatabaseName";
import {
  buildWorkspaceScopedUrl,
} from "../../../utils/openWorkspaceInApp";
import {
  getPlatformDominioPassengerReconciliation,
  getPlatformDominioPassengerMercadoPagoAuditMpPage,
  getPlatformPassengerMercadoPagoAuditMpPage,
  postPlatformPassengerMercadoPagoAuditBatch,
} from "../../../api/passagensApi";
import {
  AnalyticsDataTable,
  AnalyticsSection,
  useAnalyticsTableSort,
} from "./platformConsoleAnalyticsUi";
import ConsoleOperationSteps from "./ConsoleOperationSteps";
import { fmtChargebackLabel } from "./shared";
import { formatProductUsagePeriodBR } from "../../../utils/productUsageScreenLabel";
import { MpPaymentIdCell } from "../../Passagens/passagemMpPaymentUi";

const STATUS_COLOR = {
  ok: "success",
  warning: "warning",
  error: "error",
};

const TENANT_SORT_FIELDS = {
  workspace: { type: "string", get: (r) => r.dominio },
  local: { type: "number", get: (r) => Number(r.summary?.local_xpay_count) || 0 },
  matched: { type: "number", get: (r) => r.summary?.matched_count },
  chargebacks: { type: "number", get: (r) => r.summary?.chargebacks_count },
  refundsOk: { type: "number", get: (r) => r.summary?.refunds_ok_count },
  refundsDivergence: { type: "number", get: (r) => r.summary?.refunds_divergence_count },
  statusMismatch: { type: "number", get: (r) => r.summary?.status_mismatch_count },
  noMpId: { type: "number", get: (r) => r.summary?.no_mp_id_count ?? r.summary?.unmatched_local_count },
  amountMismatch: { type: "number", get: (r) => r.summary?.amount_mismatch_count },
  unmatchedMp: { type: "number", get: (r) => r.summary?.unmatched_mp_count },
  synced: { type: "number", get: (r) => Number(r.sync?.updated) || 0 },
  duration: { type: "number", get: (r) => Number(r.duration_ms) || 0 },
  status: { type: "string", get: (r) => r._runStatus || r.status || r.summary?.status },
};

const DETAIL_SORT_FIELDS = {
  workspace: { type: "string", get: (r) => r.workspace },
  record_type: { type: "string", get: (r) => r.record_type },
  id_lead: { type: "number", get: (r) => Number(r.id_lead) || 0 },
  mp_payment_id: { type: "string", get: (r) => r.mp_payment_id },
  localizador: { type: "string", get: (r) => r.localizador },
  passenger: { type: "string", get: (r) => r.passenger },
  local_amount: { type: "number", get: (r) => Number(r.local_amount) || 0 },
  mp_amount: { type: "number", get: (r) => Number(r.mp_amount) || 0 },
  diff: { type: "number", get: (r) => Math.abs(Number(r.diff) || 0) },
  reason: { type: "string", get: (r) => r.reason },
  local_refund_amount: { type: "number", get: (r) => Number(r.local_refund_amount) || 0 },
  mp_refund_amount: { type: "number", get: (r) => Number(r.mp_refund_amount) || 0 },
  local_payment_status: { type: "string", get: (r) => r.local_payment_status },
  mp_status: { type: "string", get: (r) => r.mp_status },
  local_status: { type: "string", get: (r) => r.local_status },
  match_method: { type: "string", get: (r) => r.match_method },
  chargeback_id: { type: "string", get: (r) => r.chargeback_id },
  chargeback_reason: { type: "string", get: (r) => r.chargeback_reason },
  documentation_status: { type: "string", get: (r) => r.documentation_status },
  documentation_deadline: { type: "string", get: (r) => r.documentation_deadline },
  mp_status_detail: { type: "string", get: (r) => r.mp_status_detail },
  mp_date: { type: "string", get: (r) => r.mp_date },
};

const RECORD_TYPE_CHIP_COLOR = {
  chargeback: "error",
  refund: "warning",
  refund_ok: "success",
  refund_divergence: "warning",
  amount_mismatch: "warning",
  status_mismatch: "warning",
  matched: "success",
  unmatched_mp: "info",
  mp_not_found: "default",
  no_mp_id: "default",
};

const MP_STATUS_CHIP_COLOR = {
  approved: "success",
  charged_back: "error",
  refunded: "warning",
  partially_refunded: "warning",
  pending: "info",
  in_process: "info",
  authorized: "info",
  cancelled: "default",
  rejected: "error",
};

function parseLeadId(value) {
  if (value == null || value === "") return null;
  const s = String(value).trim();
  const cod = s.match(/^COD-(\d+)$/i);
  if (cod) return Number(cod[1]);
  const bpe = s.match(/^BPEWEB-(\d+)$/i);
  if (bpe) return Number(bpe[1]);
  if (/^\d+$/.test(s)) return Number(s);
  return null;
}

function amountsDiffer(local, mp, tolerance = 0.02) {
  const a = Number(local);
  const b = Number(mp);
  if (!Number.isFinite(a) || !Number.isFinite(b)) return false;
  return Math.abs(a - b) > tolerance;
}

function chatPathForLead(idLead) {
  const id = parseLeadId(idLead);
  return id ? `/chat/${id}` : null;
}

function clientPathForId(idCliente) {
  const id = Number(idCliente);
  return Number.isFinite(id) && id > 0 ? `/clients/${id}` : null;
}

function resolveRowWorkspace(row, fallbackTenant, selection) {
  const dominio = row?.workspace || fallbackTenant?.dominio || selection?.dominio || null;
  const bd =
    row?.workspace_schema ||
    fallbackTenant?.schema ||
    resolveTenantDatabaseName(dominio, null) ||
    null;
  return { dominio, bd };
}

function AuditTableLink({ to, href, onNavigate, children, title }) {
  const linkSx = {
    display: "inline-flex",
    alignItems: "center",
    gap: 0.35,
    fontWeight: 600,
    maxWidth: "100%",
  };

  if (href) {
    return (
      <Tooltip title={title || ""}>
        <Link
          href={href}
          target="_blank"
          rel="noopener noreferrer"
          underline="hover"
          color="primary"
          sx={linkSx}
          onClick={(e) => e.stopPropagation()}
        >
          {children}
          <OpenInNewIcon sx={{ fontSize: 14, opacity: 0.65, flexShrink: 0 }} />
        </Link>
      </Tooltip>
    );
  }

  if (onNavigate) {
    return (
      <Tooltip title={title || ""}>
        <Link
          component="button"
          type="button"
          underline="hover"
          fontWeight={600}
          sx={{
            ...linkSx,
            border: 0,
            background: "none",
            cursor: "pointer",
            p: 0,
            font: "inherit",
            color: "primary.main",
            textAlign: "left",
          }}
          onClick={(e) => {
            e.stopPropagation();
            onNavigate();
          }}
        >
          {children}
          <OpenInNewIcon sx={{ fontSize: 14, opacity: 0.65, flexShrink: 0 }} />
        </Link>
      </Tooltip>
    );
  }
  if (!to) return children || "—";
  return (
    <Tooltip title={title || ""}>
      <Link
        component={RouterLink}
        to={to}
        underline="hover"
        fontWeight={600}
        sx={linkSx}
        onClick={(e) => e.stopPropagation()}
      >
        {children}
      </Link>
    </Tooltip>
  );
}

function StatusTransitionCell({ current, expected, t }) {
  const label = current || "—";
  if (!expected || expected === current) {
    return (
      <Stack spacing={0.25} alignItems="flex-start">
        <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.2 }}>
          {t("platformPage.passengerMpAuditStatusActual")}
        </Typography>
        <Chip size="small" variant="outlined" label={label} sx={{ maxWidth: "100%" }} />
      </Stack>
    );
  }
  return (
    <Stack spacing={0.35} alignItems="flex-start" sx={{ minWidth: 104 }}>
      <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.2 }}>
        {t("platformPage.passengerMpAuditStatusActual")}
      </Typography>
      <Chip size="small" variant="outlined" label={label} sx={{ maxWidth: "100%" }} />
      <Typography variant="caption" color="warning.main" fontWeight={600} sx={{ lineHeight: 1.2, pt: 0.25 }}>
        {t("platformPage.passengerMpAuditStatusExpected")}
      </Typography>
      <Chip size="small" color="warning" variant="filled" label={expected} sx={{ maxWidth: "100%" }} />
    </Stack>
  );
}

function AuditColumnHeader({ title, hint }) {
  if (!hint) return title;
  return (
    <Tooltip title={hint} arrow placement="top">
      <Box component="span" sx={{ borderBottom: "1px dotted", borderColor: "text.disabled", cursor: "help" }}>
        {title}
      </Box>
    </Tooltip>
  );
}

function RefundSituationCell({ row, t }) {
  if (row.refund_sync_state === "ok") {
    return (
      <Chip
        size="small"
        color="success"
        variant="filled"
        label={t("platformPage.passengerMpAuditRefundSynced")}
      />
    );
  }
  const issues = row.refund_issues || [];
  if (!issues.length) {
    return (
      <Chip
        size="small"
        color="warning"
        variant="filled"
        label={t("platformPage.passengerMpAuditRefundDivergence")}
      />
    );
  }
  return (
    <Box sx={{ minWidth: 200, maxWidth: 300 }}>
      {issues.map((issue) => (
        <Typography
          key={issue}
          variant="body2"
          color="warning.dark"
          sx={{
            display: "flex",
            alignItems: "flex-start",
            gap: 0.75,
            mb: issues.length > 1 ? 0.5 : 0,
            lineHeight: 1.4,
            fontWeight: 500,
          }}
        >
          <Box component="span" sx={{ color: "warning.main", fontWeight: 700, flexShrink: 0 }}>
            •
          </Box>
          {t(`platformPage.passengerMpAuditRefundIssue_${issue}`, { defaultValue: issue })}
        </Typography>
      ))}
    </Box>
  );
}

function MpStatusChip({ status }) {
  const raw = String(status || "").trim();
  if (!raw || raw === "—") return "—";
  const key = raw.toLowerCase();
  return (
    <Chip size="small" label={raw} color={MP_STATUS_CHIP_COLOR[key] || "default"} variant="filled" sx={{ fontWeight: 600 }} />
  );
}

function RecordTypeChip({ recordType, t }) {
  return (
    <Chip
      size="small"
      variant="filled"
      color={RECORD_TYPE_CHIP_COLOR[recordType] || "default"}
      label={t(`platformPage.passengerMpAuditIssue_${recordType}`, { defaultValue: recordType })}
      sx={{ fontWeight: 600 }}
    />
  );
}

function MoneyCell({ value, highlight = false, color }) {
  return (
    <Typography
      component="span"
      variant="body2"
      sx={{
        fontVariantNumeric: "tabular-nums",
        fontWeight: highlight ? 700 : 500,
        color: color || (highlight ? "error.main" : "text.primary"),
      }}
    >
      {fmtMoney(value)}
    </Typography>
  );
}

function getDetailRowSx(row) {
  const type = row?.record_type;
  if (type === "chargeback") {
    return (theme) => ({
      bgcolor: alpha(theme.palette.error.main, theme.palette.mode === "dark" ? 0.12 : 0.06),
      "&:hover": { bgcolor: alpha(theme.palette.error.main, theme.palette.mode === "dark" ? 0.18 : 0.1) },
    });
  }
  if (type === "amount_mismatch" || row?.has_amount_diff) {
    return (theme) => ({
      bgcolor: alpha(theme.palette.warning.main, theme.palette.mode === "dark" ? 0.12 : 0.07),
      "&:hover": { bgcolor: alpha(theme.palette.warning.main, theme.palette.mode === "dark" ? 0.18 : 0.11) },
    });
  }
  if (type === "refund" || type === "refund_ok") {
    return (theme) => ({
      bgcolor: alpha(theme.palette.success.main, theme.palette.mode === "dark" ? 0.1 : 0.06),
    });
  }
  if (type === "refund_divergence") {
    return (theme) => ({
      bgcolor: alpha(theme.palette.warning.main, theme.palette.mode === "dark" ? 0.14 : 0.09),
      "&:hover": { bgcolor: alpha(theme.palette.warning.main, theme.palette.mode === "dark" ? 0.2 : 0.12) },
    });
  }
  if (type === "refund") {
    return (theme) => ({
      bgcolor: alpha(theme.palette.warning.main, theme.palette.mode === "dark" ? 0.1 : 0.05),
    });
  }
  if (type === "status_mismatch" || type === "mp_not_found" || type === "unmatched_mp") {
    return (theme) => ({
      bgcolor: alpha(theme.palette.info.main, theme.palette.mode === "dark" ? 0.1 : 0.05),
    });
  }
  if (type === "matched") {
    return (theme) => ({
      borderLeft: "3px solid",
      borderLeftColor: theme.palette.success.main,
    });
  }
  return undefined;
}

const SUMMARY_KEYS = [
  "total",
  "ok",
  "warning",
  "error",
  "table_missing",
  "matched_count",
  "unmatched_local_count",
  "unmatched_mp_count",
  "amount_mismatch_count",
  "status_mismatch_count",
  "chargebacks_count",
  "refunds_count",
  "refunds_ok_count",
  "refunds_divergence_count",
  "synced_count",
  "no_mp_id_count",
  "mp_not_found_count",
];

const SUMMARY_AMOUNT_KEYS = [
  "local_xpay_amount",
  "matched_amount",
  "unmatched_local_amount",
  "unmatched_mp_amount",
  "amount_mismatch_amount",
  "status_mismatch_amount",
  "chargebacks_amount",
  "refunds_amount",
  "refunds_ok_amount",
  "refunds_divergence_amount",
  "no_mp_id_amount",
  "mp_not_found_amount",
];

const SUMMARY_STAT_AMOUNT_FIELD = {
  matched: "matched_amount",
  matched_count: "matched_amount",
  chargebacks_count: "chargebacks_amount",
  refunds_divergence_count: "refunds_divergence_amount",
  refunds_ok_count: "refunds_ok_amount",
  status_mismatch_count: "status_mismatch_amount",
  amount_mismatch_count: "amount_mismatch_amount",
  mp_not_found_count: "mp_not_found_amount",
  unmatched_mp_count: "unmatched_mp_amount",
  no_mp_id: "no_mp_id_amount",
  local: "unmatched_local_amount",
};

const MP_MAX_PAGES = 200;

function inferWaitingPhase(elapsedMs, requestKind = "audit") {
  if (requestKind === "sync") return "sync";
  if (elapsedMs < 8000) return "load_local";
  if (elapsedMs < 25000) return "compare";
  return "waiting_server";
}

function buildMpAuditPhaseDetail(t, phase, seconds, extra = "") {
  const keyByPhase = {
    load_local: "passengerMpAuditPhaseLoadLocal",
    compare: "passengerMpAuditPhaseCompare",
    sync: "passengerMpAuditPhaseSync",
    payment_intent_enrich: "passengerMpAuditPhasePaymentIntent",
    waiting_server: "passengerMpAuditPhaseWaitingServer",
  };
  const key = keyByPhase[phase];
  const base = key ? t(`platformPage.${key}`, { seconds }) : "";
  if (base && extra) return `${base} — ${extra}`;
  return base || extra || "";
}

function buildSyncSuccessMessage(t, { synced = 0, paymentIntentEnriched = 0 } = {}) {
  const s = Number(synced) || 0;
  const pi = Number(paymentIntentEnriched) || 0;
  if (pi > 0) {
    return t("platformPage.passengerMpAuditSyncDoneWithLinks", {
      synced: s,
      paymentIntentEnriched: pi,
    });
  }
  return t("platformPage.passengerMpAuditSyncDone", { synced: s });
}

function resolvePaymentIntentEnriched(data, summary) {
  return (
    Number(summary?.payment_intent_enriched_count) ||
    Number(data?.payment_intent?.enriched) ||
    Number(data?.sync?.payment_intent?.enriched) ||
    0
  );
}


function mergeTenantRowFromBatch(baseRow, patchRow) {
  if (!patchRow) return baseRow;
  return {
    ...baseRow,
    ...patchRow,
    summary: patchRow.summary || baseRow?.summary,
    sync: patchRow.sync ?? baseRow?.sync,
    payment_intent: patchRow.payment_intent ?? baseRow?.payment_intent ?? patchRow.sync?.payment_intent,
    progress: [...(baseRow?.progress || []), ...(patchRow?.progress || [])].filter(
      (step, idx, arr) => arr.findIndex((s) => s.step === step.step) === idx
    ),
    duration_ms: Number(baseRow?.duration_ms || 0) + Number(patchRow?.duration_ms || 0),
    ok: baseRow?.ok !== false && patchRow?.ok !== false,
  };
}

function fmtCount(v) {
  return Number(v || 0).toLocaleString("pt-BR");
}

function fmtMoney(v) {
  const n = Number(v);
  if (!Number.isFinite(n)) return "—";
  return n.toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
}

function fmtDurationMs(ms) {
  const n = Number(ms);
  if (!Number.isFinite(n) || n < 0) return "—";
  if (n < 1000) return `${Math.round(n)} ms`;
  if (n < 60000) return `${(n / 1000).toFixed(1)} s`;
  const mins = Math.floor(n / 60000);
  const secs = Math.floor((n % 60000) / 1000);
  return `${mins}m ${secs}s`;
}

function buildTenantResultRows(audit, progress) {
  const auditTenants = audit?.tenants || [];
  if (auditTenants.length) {
    return auditTenants.map((row) => ({
      ...row,
      _rowKey: String(row.dominioId),
      _runStatus: tenantProgressStatus(row, audit?.applied),
    }));
  }

  return (progress?.items || [])
    .filter((it) => it.dominioId)
    .map((it) => {
      if (it.tenantData) {
        return {
          ...it.tenantData,
          _rowKey: String(it.dominioId),
          _runStatus: it.status,
          _runDetail: it.detail,
          duration_ms: it.durationMs ?? it.tenantData.duration_ms,
        };
      }
      return {
        dominioId: it.dominioId,
        dominio: it.dominio || it.label,
        ok: it.status === "ok" || it.status === "warning",
        summary: {},
        _rowKey: String(it.dominioId),
        _runStatus: it.status,
        _runDetail: it.detail,
        duration_ms: it.durationMs,
      };
    });
}

function TenantStatCell({ value, amount, color = "default", onClick, selected, align = "right" }) {
  const n = Number(value) || 0;
  const money = Number(amount);
  const showMoney = Number.isFinite(money) && money > 0;
  const clickable = Boolean(onClick) && n > 0;
  if (!clickable) {
    return (
      <Stack spacing={0.15} alignItems={align === "right" ? "flex-end" : "flex-start"}>
        <Typography
          variant="body2"
          color={n ? "text.primary" : "text.disabled"}
          align={align}
          sx={{ fontVariantNumeric: "tabular-nums", fontWeight: n ? 600 : 400 }}
        >
          {fmtCount(n)}
        </Typography>
        {showMoney ? (
          <Typography variant="caption" color="text.secondary" sx={{ fontVariantNumeric: "tabular-nums" }}>
            {fmtMoney(money)}
          </Typography>
        ) : null}
      </Stack>
    );
  }
  return (
    <Box sx={{ display: "flex", justifyContent: align === "right" ? "flex-end" : "flex-start" }}>
      <Chip
        size="small"
        color={color}
        variant={selected ? "filled" : "outlined"}
        label={showMoney ? `${fmtCount(n)} · ${fmtMoney(money)}` : fmtCount(n)}
        onClick={(e) => {
          e.stopPropagation();
          onClick();
        }}
        sx={{
          cursor: "pointer",
          minWidth: 36,
          fontWeight: 600,
          fontVariantNumeric: "tabular-nums",
          height: "auto",
          py: 0.35,
          "& .MuiChip-label": { whiteSpace: "normal", lineHeight: 1.25 },
          ...(selected ? { boxShadow: 2, outline: "2px solid", outlineColor: "primary.main" } : {}),
        }}
      />
    </Box>
  );
}

function TenantRunStatusCell({ row, t }) {
  const runStatus = row._runStatus;
  if (runStatus === "running" || runStatus === "pending") {
    return (
      <Stack direction="row" spacing={0.75} alignItems="center">
        {runStatus === "running" ? <CircularProgress size={14} /> : null}
        <Chip size="small" color={runStatus === "running" ? "info" : "default"} variant="outlined" label={runStatus} />
      </Stack>
    );
  }

  const issues = countAuditIssues(row.summary);
  const chipStatus = !row.ok
    ? row.code === "timeout"
      ? "warning"
      : "error"
    : issues > 0
      ? "warning"
      : "ok";

  return (
    <Tooltip title={row._runDetail || ""} disableHoverListener={!row._runDetail}>
      <Chip size="small" color={STATUS_COLOR[chipStatus] || "default"} label={chipStatus} />
    </Tooltip>
  );
}

function MpAuditTenantResultsTable({
  rows,
  t,
  sortBy,
  sortOrder,
  onSort,
  sortFields,
  onStatClick,
  selectedStat,
  showSyncColumn = false,
  emptyLabel,
}) {
  const statColumns = [
    { id: "local", statKey: "local", label: t("platformPage.passengerMpAuditStatLocal"), color: "default", field: "local_xpay_count", amountField: "local_xpay_amount" },
    { id: "matched", statKey: "matched", label: t("platformPage.passengerMpAuditMatched"), color: "success", field: "matched_count", amountField: "matched_amount" },
    { id: "chargebacks", statKey: "chargebacks_count", label: t("platformPage.passengerMpAuditChargebacks"), color: "error", field: "chargebacks_count", amountField: "chargebacks_amount" },
    { id: "refundsOk", statKey: "refunds_ok_count", label: t("platformPage.passengerMpAuditRefundsOkShort"), color: "success", field: "refunds_ok_count", amountField: "refunds_ok_amount" },
    { id: "refundsDivergence", statKey: "refunds_divergence_count", label: t("platformPage.passengerMpAuditRefundsDivergenceShort"), color: "warning", field: "refunds_divergence_count", amountField: "refunds_divergence_amount" },
    { id: "statusMismatch", statKey: "status_mismatch_count", label: t("platformPage.passengerMpAuditStatusMismatch"), color: "warning", field: "status_mismatch_count", amountField: "status_mismatch_amount" },
    { id: "noMpId", statKey: "no_mp_id", label: t("platformPage.passengerMpAuditIssue_no_mp_id"), color: "default", field: "no_mp_id_count", fallbackField: "unmatched_local_count", amountField: "no_mp_id_amount" },
    { id: "amountMismatch", statKey: "amount_mismatch_count", label: t("platformPage.passengerMpAuditAmountMismatch"), color: "warning", field: "amount_mismatch_count", amountField: "amount_mismatch_amount" },
    { id: "unmatchedMp", statKey: "unmatched_mp_count", label: t("platformPage.passengerMpAuditUnmatchedMp"), color: "default", field: "unmatched_mp_count", amountField: "unmatched_mp_amount" },
  ];

  const columns = [
    {
      id: "workspace",
      label: t("platformPage.productUsageColWorkspace"),
      cellSx: { minWidth: 148 },
      render: (row) => (
        <Typography variant="body2" fontWeight={600} sx={{ wordBreak: "break-word" }}>
          {row.dominio || "—"}
        </Typography>
      ),
    },
    ...statColumns.map((col) => ({
      id: col.id,
      label: col.label,
      align: "right",
      cellSx: { minWidth: 72, whiteSpace: "nowrap" },
      render: (row) => {
        const value =
          row.summary?.[col.field] ??
          (col.fallbackField ? row.summary?.[col.fallbackField] : undefined) ??
          0;
        const amount = col.amountField ? row.summary?.[col.amountField] : null;
        const selected =
          selectedStat?.dominioId === row.dominioId && selectedStat?.statKey === col.statKey;
        return (
          <TenantStatCell
            value={value}
            amount={amount}
            color={col.color}
            selected={selected}
            onClick={
              onStatClick
                ? () =>
                    onStatClick(row, {
                      key: col.statKey,
                      label: col.label,
                    })
                : null
            }
          />
        );
      },
    })),
  ];

  if (showSyncColumn) {
    columns.push({
      id: "synced",
      label: t("platformPage.passengerAuditStep_sync"),
      align: "right",
      cellSx: { minWidth: 72 },
      render: (row) => {
        if (row.sync?.skipped) {
          return (
            <Typography variant="caption" color="text.secondary">
              {t("platformPage.passengerMpAuditSyncSkippedEmpty")}
            </Typography>
          );
        }
        if (row.sync?.hasMore) {
          return (
            <Chip
              size="small"
              color="warning"
              variant="outlined"
              label={t("platformPage.passengerMpAuditTenantSyncPartial", {
                updated: row.sync?.updated || 0,
                remaining: row.sync?.remaining || 0,
              })}
            />
          );
        }
        return <TenantStatCell value={row.sync?.updated} color="info" />;
      },
    });
  }

  columns.push(
    {
      id: "duration",
      label: t("platformPage.passengerMpAuditColDuration"),
      align: "right",
      cellSx: { minWidth: 72, whiteSpace: "nowrap" },
      render: (row) => (
        <Typography variant="body2" color="text.secondary" sx={{ fontVariantNumeric: "tabular-nums" }}>
          {fmtDurationMs(row.duration_ms)}
        </Typography>
      ),
    },
    {
      id: "status",
      label: t("passagensPage.colStatus"),
      cellSx: { minWidth: 88, whiteSpace: "nowrap" },
      render: (row) => <TenantRunStatusCell row={row} t={t} />,
    }
  );

  return (
    <AnalyticsDataTable
      rows={rows}
      emptyLabel={emptyLabel}
      columns={columns}
      sortBy={sortBy}
      sortOrder={sortOrder}
      onSort={onSort}
      sortFields={sortFields}
      getRowSx={(row) => {
        if (row._runStatus === "running") {
          return (theme) => ({
            bgcolor: alpha(theme.palette.info.main, theme.palette.mode === "dark" ? 0.1 : 0.05),
          });
        }
        if (row._runStatus === "pending") {
          return (theme) => ({
            bgcolor: alpha(theme.palette.action.hover, theme.palette.mode === "dark" ? 0.08 : 0.04),
          });
        }
        if (countAuditIssues(row.summary) > 0) {
          return (theme) => ({
            bgcolor: alpha(theme.palette.warning.main, theme.palette.mode === "dark" ? 0.08 : 0.04),
          });
        }
        return undefined;
      }}
    />
  );
}

const STAT_KEY_TO_SUMMARY_FIELD = {
  local: "local_xpay_count",
  matched: "matched_count",
  amount_mismatch_count: "amount_mismatch_count",
  status_mismatch_count: "status_mismatch_count",
  chargebacks_count: "chargebacks_count",
  refunds_count: "refunds_count",
  refunds_ok_count: "refunds_ok_count",
  refunds_divergence_count: "refunds_divergence_count",
  unmatched_mp_count: "unmatched_mp_count",
  no_mp_id: "no_mp_id_count",
  mp_not_found_count: "mp_not_found_count",
};

function normalizeMatchedRow(row) {
  const venda = row?.venda || {};
  const mp = row?.mp || {};
  const localAmount = venda.valor_cobrado;
  const mpAmount = mp.transaction_amount;
  return {
    _rowKey: `m-${venda.id || "x"}-${mp.id || row.mp_status}`,
    record_type: "matched",
    id_lead: venda.id_lead,
    id_cliente: venda.id_cliente,
    mp_payment_id: venda.mp_payment_id || mp.id,
    localizador: venda.localizador,
    passenger: venda.nome_passageiro,
    local_amount: localAmount,
    mp_amount: mpAmount,
    has_amount_diff: amountsDiffer(localAmount, mpAmount),
    mp_status: row.mp_status || mp.status,
    local_status: venda.status_venda,
    match_method: row.matchMethod,
  };
}

function normalizeAmountMismatchRow(row) {
  const venda = row?.venda || {};
  const mp = row?.mp || {};
  const cmp = row?.amountCompare || {};
  return {
    _rowKey: `am-${venda.id}-${mp.id}`,
    record_type: "amount_mismatch",
    id_lead: venda.id_lead,
    id_cliente: venda.id_cliente,
    mp_payment_id: venda.mp_payment_id || mp.id,
    localizador: venda.localizador,
    passenger: venda.nome_passageiro,
    local_amount: cmp.localAmt ?? row.localAmt ?? venda.valor_cobrado,
    mp_amount: cmp.mpAmt ?? row.mpAmt ?? mp.transaction_amount,
    diff: cmp.diff ?? row.amountDiff,
    mp_status: mp.status,
    local_status: venda.status_venda,
    match_method: row.matchMethod,
    reason: row.reason,
  };
}

function normalizeStatusMismatchRow(row) {
  const venda = row?.venda || {};
  const mp = row?.mp || {};
  return {
    _rowKey: `sm-${venda.id}-${mp.id}`,
    record_type: "status_mismatch",
    id_lead: venda.id_lead,
    id_cliente: venda.id_cliente,
    mp_payment_id: venda.mp_payment_id || mp.id,
    localizador: venda.localizador,
    passenger: venda.nome_passageiro,
    mp_status: row.mp_status || mp.status,
    local_status: row.local_venda || venda.status_venda,
    expected_status: row.expected_venda,
    match_method: row.matchMethod,
  };
}

function normalizeRefundRow(row) {
  const venda = row?.venda || {};
  const mp = row?.mp || {};
  const assess = row?.refundAssess || {};
  const syncState = row?.sync_state || assess?.sync_state || "divergence";
  const recordType = syncState === "ok" ? "refund_ok" : "refund_divergence";
  return {
    _rowKey: `${recordType}-${venda.id}-${mp.id || row.mp_status}`,
    record_type: recordType,
    refund_sync_state: syncState,
    refund_issues: assess?.issues || [],
    id_lead: venda.id_lead,
    id_cliente: venda.id_cliente,
    mp_payment_id: venda.mp_payment_id || mp.id,
    localizador: venda.localizador,
    passenger: venda.nome_passageiro,
    local_amount: venda.valor_cobrado,
    mp_amount: mp.transaction_amount,
    local_refund_amount: assess?.local_refund_amount ?? venda.valor_estornado,
    mp_refund_amount: assess?.mp_refund_amount ?? mp.transaction_amount_refunded,
    mp_status: row.mp_status || mp.status,
    local_status: venda.status_venda,
    local_payment_status: assess?.local_status_pagamento || venda.status_pagamento,
    expected_status: assess?.expected_status_venda,
    expected_payment_status: assess?.expected_status_pagamento,
    is_partial_refund: assess?.is_partial ?? row.mp_status === "partially_refunded",
  };
}

function filterRefundRows(tenant, syncState) {
  return (tenant?.refunds || [])
    .filter((row) => (row?.sync_state || row?.refundAssess?.sync_state) === syncState)
    .map(normalizeRefundRow);
}

function normalizeChargebackRow(row, recordType = "chargeback") {
  const venda = row?.venda || {};
  const mp = row?.mp || {};
  const primaryCb = Array.isArray(row?.mp_chargebacks) ? row.mp_chargebacks[0] : null;
  return {
    _rowKey: `${recordType}-${venda.id}-${mp.id || row.mp_status}-${primaryCb?.id || ""}`,
    record_type: recordType,
    id_lead: venda.id_lead,
    id_cliente: venda.id_cliente,
    mp_payment_id: venda.mp_payment_id || mp.id,
    localizador: venda.localizador,
    passenger: venda.nome_passageiro,
    local_amount: venda.valor_cobrado,
    mp_amount: mp.transaction_amount,
    mp_status: row.mp_status || mp.status,
    mp_status_detail: row.mp_status_detail || mp.status_detail || null,
    local_status: venda.status_venda,
    chargeback_id: primaryCb?.id || null,
    chargeback_reason: primaryCb?.reason || null,
    documentation_required: primaryCb?.documentation_required ?? null,
    documentation_status: primaryCb?.documentation_status || null,
    documentation_deadline: primaryCb?.date_documentation_deadline || null,
    coverage_applied: primaryCb?.coverage_applied ?? null,
    coverage_elegible: primaryCb?.coverage_elegible ?? null,
    chargeback_amount: primaryCb?.amount ?? null,
    chargeback_count: Array.isArray(row?.mp_chargebacks) ? row.mp_chargebacks.length : 0,
  };
}

function normalizeUnmatchedLocalRow(row) {
  const venda = row?.venda || {};
  return {
    _rowKey: `ul-${row.type}-${venda.id}`,
    record_type: row.type || "unmatched_local",
    id_lead: venda.id_lead,
    id_cliente: venda.id_cliente,
    mp_payment_id: venda.mp_payment_id || row.mp_payment_id,
    localizador: venda.localizador,
    passenger: venda.nome_passageiro,
    local_amount: venda.valor_cobrado,
    mp_status: "—",
    local_status: venda.status_venda,
  };
}

function normalizeUnmatchedMpRow(row) {
  const leadId = parseLeadId(row.external_reference);
  return {
    _rowKey: `ump-${row.mp_payment_id}`,
    record_type: "unmatched_mp",
    id_lead: leadId,
    id_cliente: null,
    mp_payment_id: row.mp_payment_id,
    localizador: "—",
    passenger: "—",
    mp_amount: row.amount,
    mp_status: row.status,
    local_status: "—",
    mp_date: row.date_created,
  };
}

function extractDetailRows(tenant, statKey) {
  if (!tenant) return [];
  let rows = [];
  switch (statKey) {
    case "local":
      rows = [
        ...(tenant.matched || []).map(normalizeMatchedRow),
        ...(tenant.unmatchedLocal || []).map(normalizeUnmatchedLocalRow),
      ];
      break;
    case "matched":
      rows = (tenant.matched || []).map(normalizeMatchedRow);
      break;
    case "amount_mismatch_count":
      rows = (tenant.amountMismatch || []).map(normalizeAmountMismatchRow);
      break;
    case "status_mismatch_count":
      rows = (tenant.statusMismatch || []).map(normalizeStatusMismatchRow);
      break;
    case "chargebacks_count":
      rows = (tenant.chargebacks || []).map((r) => normalizeChargebackRow(r, "chargeback"));
      break;
    case "refunds_count":
      rows = (tenant.refunds || []).map(normalizeRefundRow);
      break;
    case "refunds_ok_count":
      rows = filterRefundRows(tenant, "ok");
      break;
    case "refunds_divergence_count":
      rows = filterRefundRows(tenant, "divergence");
      break;
    case "unmatched_mp_count":
      rows = (tenant.unmatchedMp || []).map(normalizeUnmatchedMpRow);
      break;
    case "no_mp_id":
      rows = (tenant.unmatchedLocal || [])
        .filter((r) => r.type === "no_mp_id")
        .map(normalizeUnmatchedLocalRow);
      break;
    case "mp_not_found_count":
      rows = (tenant.unmatchedLocal || [])
        .filter((r) => r.type === "mp_not_found")
        .map(normalizeUnmatchedLocalRow);
      break;
    default:
      rows = [];
  }
  return rows.map((row) => ({
    ...row,
    workspace: tenant.dominio || row.workspace,
    workspace_schema: tenant.schema || row.workspace_schema,
    dominioId: tenant.dominioId ?? row.dominioId,
  }));
}

function getDetailTotalCount(source, statKey) {
  const field = STAT_KEY_TO_SUMMARY_FIELD[statKey];
  if (!field) return 0;
  if (Array.isArray(source)) {
    return source.reduce((sum, t) => sum + Number(t.summary?.[field] || 0), 0);
  }
  return Number(source.summary?.[field] || 0);
}

function aggregateDetailRows(tenants, statKey) {
  return (tenants || []).flatMap((tenant) =>
    extractDetailRows(tenant, statKey).map((row) => ({
      ...row,
      _rowKey: `${tenant.dominioId}-${row._rowKey}`,
    }))
  );
}

function buildDetailTableColumns(statKey, t, { showWorkspace = false, resolveWorkspace = null } = {}) {
  const isRefundStat =
    statKey === "refunds_count" || statKey === "refunds_ok_count" || statKey === "refunds_divergence_count";

  const protocolHref = (row, leadId) => {
    if (!resolveWorkspace || !leadId) return null;
    const { bd, dominio } = resolveWorkspace(row);
    return buildWorkspaceScopedUrl(bd, dominio, `/popup-call/${leadId}`);
  };

  const clientHref = (row, clientId) => {
    if (!resolveWorkspace || !clientId) return null;
    const { bd, dominio } = resolveWorkspace(row);
    return buildWorkspaceScopedUrl(bd, dominio, `/clients/${clientId}`);
  };

  const cols = [];
  if (showWorkspace) {
    cols.push({
      id: "workspace",
      label: t("platformPage.productUsageColWorkspace"),
      cellSx: { minWidth: 140 },
      render: (row) => (
        <Typography variant="body2" fontWeight={600}>
          {row.workspace || "—"}
        </Typography>
      ),
    });
  }
  cols.push({
    id: "record_type",
    label: t("platformPage.passengerMpAuditColType"),
    cellSx: { minWidth: 130, whiteSpace: "nowrap" },
    render: (row) => <RecordTypeChip recordType={row.record_type} t={t} />,
  });
  cols.push({
    id: "id_lead",
    label: t("platformPage.passengerMpAuditColProtocolo"),
    cellSx: { minWidth: 96, whiteSpace: "nowrap" },
    render: (row) => {
      const leadId = parseLeadId(row.id_lead);
      if (!leadId) return "—";
      const href = protocolHref(row, leadId);
      return (
        <AuditTableLink
          href={href}
          to={href ? null : chatPathForLead(leadId)}
          title={t("platformPage.passengerMpAuditOpenProtocolPopup")}
        >
          #{leadId}
        </AuditTableLink>
      );
    },
  });

  if (!isRefundStat) {
    cols.push({
      id: "mp_payment_id",
      label: "MP ID",
      cellSx: { minWidth: 112 },
      render: (row) => (
        <MpPaymentIdCell
          value={row.mp_payment_id}
          title={t("platformPage.passengerMpAuditOpenMpPayment", { defaultValue: "Abrir venda no Mercado Pago" })}
        />
      ),
    });
  }

  cols.push({
    id: "localizador",
    label: t("passagensPage.colLocator", { defaultValue: "Localizador" }),
    cellSx: { minWidth: 88 },
    render: (row) =>
      row.localizador ? (
        <Chip size="small" variant="outlined" label={row.localizador} sx={{ fontWeight: 600, maxWidth: "100%" }} />
      ) : (
        "—"
      ),
  });
  cols.push({
    id: "passenger",
    label: t("passagensPage.colClient"),
    cellSx: { minWidth: 120 },
    render: (row) => {
      const clientPath = clientPathForId(row.id_cliente);
      const label = row.passenger || "—";
      if (!clientPath || label === "—") return label;
      const clientId = Number(row.id_cliente);
      const href = clientHref(row, clientId);
      return (
        <AuditTableLink
          href={href}
          to={href ? null : clientPath}
          title={t("platformPage.passengerMpAuditOpenClient")}
        >
          {label}
        </AuditTableLink>
      );
    },
  });

  if (isRefundStat) {
    cols.push({
      id: "refund_issues",
      label: t("platformPage.passengerMpAuditColRefundSituation"),
      cellSx: { minWidth: 220, maxWidth: 320, verticalAlign: "top" },
      render: (row) => <RefundSituationCell row={row} t={t} />,
    });
    cols.push(
      {
        id: "local_refund_amount",
        label: t("platformPage.passengerMpAuditColLocalRefund"),
        align: "right",
        cellSx: { minWidth: 108, whiteSpace: "nowrap" },
        render: (row) => (
          <MoneyCell
            value={row.local_refund_amount}
            highlight={row.refund_sync_state === "divergence" && !row.local_refund_amount}
          />
        ),
      },
      {
        id: "mp_refund_amount",
        label: t("platformPage.passengerMpAuditColMpRefund"),
        align: "right",
        cellSx: { minWidth: 108, whiteSpace: "nowrap" },
        render: (row) => <MoneyCell value={row.mp_refund_amount} />,
      }
    );
  }

  if (statKey === "amount_mismatch_count" || statKey === "local" || statKey === "matched") {
    cols.push(
      {
        id: "local_amount",
        label: t("platformPage.passengerMpAuditColLocalAmount"),
        align: "right",
        render: (row) => <MoneyCell value={row.local_amount} highlight={row.has_amount_diff || statKey === "amount_mismatch_count"} />,
      },
      {
        id: "mp_amount",
        label: t("platformPage.passengerMpAuditColMpAmount"),
        align: "right",
        render: (row) => <MoneyCell value={row.mp_amount} highlight={row.has_amount_diff || statKey === "amount_mismatch_count"} />,
      }
    );
  }
  if (statKey === "amount_mismatch_count") {
    cols.push(
      {
        id: "diff",
        label: t("platformPage.passengerMpAuditColDiff"),
        align: "right",
        render: (row) => (
          <Chip
            size="small"
            color="warning"
            variant="filled"
            label={fmtMoney(row.diff)}
            sx={{ fontWeight: 700, fontVariantNumeric: "tabular-nums" }}
          />
        ),
      },
      {
        id: "reason",
        label: t("platformPage.passengerMpAuditColReason"),
        render: (row) =>
          row.reason ? (
            <Typography variant="caption" color="text.secondary" sx={{ maxWidth: 220, display: "block" }}>
              {row.reason}
            </Typography>
          ) : (
            "—"
          ),
      }
    );
  }
  if (statKey === "unmatched_mp_count") {
    cols.push({
      id: "mp_amount",
      label: t("platformPage.passengerMpAuditColMpAmount"),
      align: "right",
      render: (row) => <MoneyCell value={row.mp_amount} />,
    });
  }

  if (statKey === "chargebacks_count") {
    cols.push(
      {
        id: "chargeback_id",
        label: t("platformPage.passengerMpAuditColChargebackId"),
        render: (row) =>
          row.chargeback_id ? (
            <Chip size="small" color="error" variant="outlined" label={row.chargeback_id} sx={{ fontWeight: 600 }} />
          ) : (
            "—"
          ),
      },
      {
        id: "chargeback_reason",
        label: t("platformPage.passengerMpAuditColChargebackReason"),
        render: (row) => fmtChargebackLabel(row.chargeback_reason, t),
      },
      {
        id: "documentation_status",
        label: t("platformPage.passengerMpAuditColDocStatus"),
        render: (row) => {
          if (row.documentation_required == null && !row.documentation_status) return "—";
          const label = [
            row.documentation_required ? t("platformPage.passengerMpAuditDocRequired") : null,
            row.documentation_status ? fmtChargebackLabel(row.documentation_status, t) : null,
          ]
            .filter(Boolean)
            .join(" · ");
          return (
            <Chip
              size="small"
              color={row.documentation_required ? "warning" : "default"}
              variant={row.documentation_required ? "filled" : "outlined"}
              label={label}
            />
          );
        },
      },
      {
        id: "documentation_deadline",
        label: t("platformPage.passengerMpAuditColDocDeadline"),
        render: (row) =>
          row.documentation_deadline ? (
            <Typography variant="body2" color="warning.main" fontWeight={600}>
              {row.documentation_deadline}
            </Typography>
          ) : (
            "—"
          ),
      },
      {
        id: "coverage",
        label: t("platformPage.passengerMpAuditColCoverage"),
        render: (row) => {
          if (row.coverage_applied == null && row.coverage_elegible == null) return "—";
          const parts = [];
          if (row.coverage_elegible != null) {
            parts.push(
              row.coverage_elegible
                ? t("platformPage.passengerMpAuditCoverageEligible")
                : t("platformPage.passengerMpAuditCoverageNotEligible")
            );
          }
          if (row.coverage_applied != null) {
            parts.push(
              row.coverage_applied
                ? t("platformPage.passengerMpAuditCoverageApplied")
                : t("platformPage.passengerMpAuditCoverageNotApplied")
            );
          }
          return (
            <Chip
              size="small"
              color={row.coverage_applied ? "success" : "default"}
              variant="outlined"
              label={parts.join(" · ")}
            />
          );
        },
      },
      {
        id: "mp_status_detail",
        label: t("platformPage.passengerMpAuditColMpStatusDetail"),
        render: (row) => row.mp_status_detail || "—",
      }
    );
  }

  cols.push(
    {
      id: "mp_status",
      label: t("platformPage.passengerMpAuditColMpStatus"),
      cellSx: { minWidth: 96, whiteSpace: "nowrap" },
      render: (row) => <MpStatusChip status={row.mp_status} />,
    },
    {
      id: "local_status",
      label: (
        <AuditColumnHeader
          title={t("platformPage.passengerMpAuditColLocalStatus")}
          hint={t("platformPage.passengerMpAuditColLocalStatusHint")}
        />
      ),
      cellSx: { minWidth: 116, verticalAlign: "top" },
      render: (row) => (
        <StatusTransitionCell current={row.local_status} expected={row.expected_status} t={t} />
      ),
    }
  );

  if (isRefundStat) {
    cols.push({
      id: "local_payment_status",
      label: (
        <AuditColumnHeader
          title={t("platformPage.passengerMpAuditColLocalPaymentStatus")}
          hint={t("platformPage.passengerMpAuditColLocalPaymentStatusHint")}
        />
      ),
      cellSx: { minWidth: 116, verticalAlign: "top" },
      render: (row) => (
        <StatusTransitionCell
          current={row.local_payment_status}
          expected={row.expected_payment_status}
          t={t}
        />
      ),
    });
    cols.push({
      id: "mp_payment_id",
      label: "MP ID",
      cellSx: { minWidth: 112 },
      render: (row) => (
        <MpPaymentIdCell
          value={row.mp_payment_id}
          title={t("platformPage.passengerMpAuditOpenMpPayment", { defaultValue: "Abrir venda no Mercado Pago" })}
        />
      ),
    });
  }

  if (statKey === "matched" || statKey === "amount_mismatch_count" || statKey === "status_mismatch_count") {
    cols.push({
      id: "match_method",
      label: t("platformPage.passengerMpAuditColMatchMethod"),
      render: (row) =>
        row.match_method ? (
          <Chip size="small" variant="outlined" color="info" label={row.match_method} />
        ) : (
          "—"
        ),
    });
  }

  if (statKey === "unmatched_mp_count") {
    cols.push({
      id: "mp_date",
      label: t("platformPage.passengerMpAuditColMpDate"),
      render: (row) => row.mp_date || "—",
    });
  }

  return cols;
}

function MpAuditDetailDialog({ selection, tenants, singleTenantAudit, t, onClose }) {
  const open = Boolean(selection);
  const showWorkspace = selection?.scope === "global";

  const resolveWorkspace = useCallback(
    (row) => {
      const tenant =
        singleTenantAudit &&
        (!selection?.dominioId || Number(singleTenantAudit.dominioId) === Number(selection.dominioId))
          ? singleTenantAudit
          : tenants?.find((item) => Number(item.dominioId) === Number(row?.dominioId || selection?.dominioId));
      return resolveRowWorkspace(row, tenant, selection);
    },
    [selection, singleTenantAudit, tenants]
  );

  const rows = useMemo(() => {
    if (!selection) return [];
    if (selection.scope === "global") {
      return aggregateDetailRows(tenants, selection.statKey);
    }
    const tenant =
      singleTenantAudit &&
      (!selection.dominioId || Number(singleTenantAudit.dominioId) === Number(selection.dominioId))
        ? singleTenantAudit
        : tenants?.find((row) => Number(row.dominioId) === Number(selection.dominioId));
    return extractDetailRows(tenant, selection.statKey);
  }, [selection, tenants, singleTenantAudit]);

  const totalCount = useMemo(() => {
    if (!selection) return 0;
    if (selection.scope === "global") {
      return getDetailTotalCount(tenants, selection.statKey);
    }
    const tenant =
      singleTenantAudit &&
      (!selection.dominioId || Number(singleTenantAudit.dominioId) === Number(selection.dominioId))
        ? singleTenantAudit
        : tenants?.find((row) => Number(row.dominioId) === Number(selection.dominioId));
    return getDetailTotalCount(tenant, selection.statKey);
  }, [selection, tenants, singleTenantAudit]);

  const truncated = rows.length > 0 && totalCount > rows.length;
  const detailSortKey = selection?.statKey ? `passenger-mp-audit-detail-${selection.statKey}` : "passenger-mp-audit-detail";
  const defaultDetailSort =
    selection?.statKey === "amount_mismatch_count"
      ? { sortBy: "diff", sortOrder: "desc" }
      : selection?.statKey === "chargebacks_count"
        ? { sortBy: "documentation_deadline", sortOrder: "asc" }
        : { sortBy: "id_lead", sortOrder: "desc" };
  const { sortBy, sortOrder, handleSort, sortRows } = useAnalyticsTableSort(
    detailSortKey,
    DETAIL_SORT_FIELDS,
    defaultDetailSort
  );
  const displayRows = useMemo(() => sortRows(rows), [rows, sortRows]);
  const columns = useMemo(
    () =>
      selection
        ? buildDetailTableColumns(selection.statKey, t, {
            showWorkspace,
            resolveWorkspace,
          })
        : [],
    [selection, t, showWorkspace, resolveWorkspace]
  );

  const title = selection
    ? selection.scope === "global"
      ? selection.statLabel
      : t("platformPage.passengerMpAuditDetailTitle", {
          label: selection.statLabel,
          dominio: selection.dominio || selection.dominioId,
        })
    : "";

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xl" fullWidth scroll="paper">
      <DialogTitle sx={{ pr: 6 }}>
        <Typography variant="h6" component="span">
          {title}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          {truncated
            ? t("platformPage.passengerMpAuditDetailTruncated", {
                shown: fmtCount(rows.length),
                total: fmtCount(totalCount),
              })
            : t("platformPage.passengerMpAuditDetailCount", { count: fmtCount(rows.length) })}
        </Typography>
        <IconButton
          aria-label={t("platformPage.passengerMpAuditDetailClose")}
          onClick={onClose}
          sx={{ position: "absolute", right: 8, top: 8 }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers sx={{ p: 0 }}>
        {displayRows.length ? (
          <Box sx={{ p: 1.5 }}>
            <AnalyticsDataTable
              rows={displayRows}
              emptyLabel={t("platformPage.passengerMpAuditIssuesEmpty")}
              columns={columns}
              sortBy={sortBy}
              sortOrder={sortOrder}
              onSort={handleSort}
              sortFields={DETAIL_SORT_FIELDS}
              getRowSx={getDetailRowSx}
            />
          </Box>
        ) : (
          <Alert severity="info" sx={{ m: 2 }}>
            {t("platformPage.passengerMpAuditDetailNoRows")}
          </Alert>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 1.5 }}>
        <Button onClick={onClose} color="inherit">
          {t("platformPage.passengerMpAuditDetailClose")}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function emptySummary() {
  return Object.fromEntries([
    ...SUMMARY_KEYS.map((k) => [k, 0]),
    ...SUMMARY_AMOUNT_KEYS.map((k) => [k, 0]),
  ]);
}

function mergeSummary(target, source) {
  const next = { ...target };
  for (const key of SUMMARY_KEYS) {
    next[key] = Number(next[key] || 0) + Number(source?.[key] || 0);
  }
  for (const key of SUMMARY_AMOUNT_KEYS) {
    next[key] = Math.round((Number(next[key] || 0) + Number(source?.[key] || 0)) * 100) / 100;
  }
  return next;
}

function fmtSummaryChipLabel(label, count, amount) {
  const n = Number(amount);
  if (Number.isFinite(n) && n > 0) {
    return `${label}: ${fmtCount(count)} · ${fmtMoney(n)}`;
  }
  return `${label}: ${fmtCount(count)}`;
}

function countAuditIssues(summary) {
  if (!summary) return 0;
  return (
    Number(summary.mp_not_found_count || 0) +
    Number(summary.unmatched_mp_count || 0) +
    Number(summary.status_mismatch_count || 0) +
    Number(summary.chargebacks_count || 0) +
    Number(summary.refunds_divergence_count || 0) +
    Number(summary.amount_mismatch_count || 0)
  );
}

function buildTenantDisplayStats(row, t, apply = false) {
  const s = row?.summary || {};
  const stats = [];
  const loadStep = (row?.progress || []).find((p) => p.step === "load_local");
  const localMatch = loadStep?.detail?.match(/^([\d.,]+)/);
  const local =
    Number(s.local_xpay_count ?? 0) ||
    (localMatch ? Number(String(localMatch[1]).replace(/\./g, "").replace(",", ".")) : 0);

  stats.push({
    key: "local",
    label: t("platformPage.passengerMpAuditStatLocal"),
    value: fmtCount(local),
    clickable: local > 0,
  });
  stats.push({
    key: "matched",
    label: t("platformPage.passengerMpAuditMatched"),
    value: fmtCount(s.matched_count),
    color: Number(s.matched_count) > 0 ? "success" : "default",
    clickable: Number(s.matched_count) > 0,
  });

  const issueFields = [
    ["mp_not_found_count", "passengerMpAuditIssue_mp_not_found", "warning"],
    ["status_mismatch_count", "passengerMpAuditStatusMismatch", "warning"],
    ["amount_mismatch_count", "passengerMpAuditAmountMismatch", "warning"],
    ["chargebacks_count", "passengerMpAuditChargebacks", "error"],
    ["refunds_divergence_count", "passengerMpAuditRefundsDivergence", "warning"],
    ["unmatched_mp_count", "passengerMpAuditUnmatchedMp", "default"],
  ];
  for (const [field, i18nKey, color] of issueFields) {
    const n = Number(s[field] || 0);
    if (n > 0) {
      stats.push({
        key: field,
        label: t(`platformPage.${i18nKey}`),
        value: fmtCount(n),
        color,
        clickable: true,
      });
    }
  }

  if (Number(s.refunds_ok_count) > 0) {
    stats.push({
      key: "refunds_ok_count",
      label: t("platformPage.passengerMpAuditRefundsOk"),
      value: fmtCount(s.refunds_ok_count),
      color: "success",
      clickable: true,
    });
  }

  if (Number(s.no_mp_id_count) > 0) {
    stats.push({
      key: "no_mp_id",
      label: t("platformPage.passengerMpAuditIssue_no_mp_id"),
      value: fmtCount(s.no_mp_id_count),
      variant: "outlined",
      clickable: true,
    });
  }

  if (apply && row?.sync) {
    if (row.sync.skipped) {
      stats.push({
        key: "sync",
        label: t("platformPage.passengerAuditStep_sync"),
        value: t("platformPage.passengerMpAuditSyncSkippedEmpty"),
        variant: "outlined",
      });
    } else if (Number(row.sync.updated) > 0) {
      stats.push({
        key: "sync",
        label: t("platformPage.passengerAuditStep_sync"),
        value: fmtCount(row.sync.updated),
        color: "success",
      });
    } else if (row.sync.hasMore) {
      stats.push({
        key: "sync",
        label: t("platformPage.passengerAuditStep_sync"),
        value: t("platformPage.passengerMpAuditTenantSyncPartial", {
          updated: row.sync.updated || 0,
          remaining: row.sync.remaining || 0,
        }),
        color: "warning",
      });
    }
    const piEnriched = Number(row.payment_intent?.enriched || row.sync?.payment_intent?.enriched || 0);
    if (piEnriched > 0) {
      stats.push({
        key: "payment_intent",
        label: t("platformPage.passengerAuditStep_payment_intent"),
        value: fmtCount(piEnriched),
        color: "info",
      });
    }
  }

  return stats;
}

function buildMpSearchStats(count, t) {
  return [
    {
      key: "mp_total",
      label: t("platformPage.passengerMpAuditMpPayments"),
      value: fmtCount(count),
      color: "primary",
    },
  ];
}

function MpAuditCrossTabComparison({ salesTotals, salesPeriod, auditSummary, audit, from, to, t }) {
  if (!auditSummary && !salesTotals) return null;

  const periodFrom = salesPeriod?.from || audit?.period?.from || from;
  const periodTo = salesPeriod?.to || audit?.period?.to || to;
  const periodLabel = periodFrom && periodTo ? formatProductUsagePeriodBR(periodFrom, periodTo) : null;

  const indexTickets = Number(salesTotals?.tickets || 0);
  const indexGmv = Number(salesTotals?.gmv || 0);
  const xpayIndexTickets = Number(salesTotals?.xpay_tickets || 0);
  const xpayIndexGmv = Number(salesTotals?.xpay_gmv || 0);
  const localTickets = Number(auditSummary?.local_xpay_count || 0);
  const localGmv = Number(auditSummary?.local_xpay_amount || 0);
  const mpTickets = Number(audit?.mp_payments_in_range ?? auditSummary?.mp_payments_in_range ?? 0);
  const mpGmv = Number(audit?.mp_payments_amount ?? auditSummary?.mp_payments_amount ?? 0);
  const matchedTickets = Number(auditSummary?.matched_count || 0);
  const matchedGmv = Number(auditSummary?.matched_amount || 0);
  const issueTickets = countAuditIssues(auditSummary);
  const issueGmv = Math.round(
    (
      Number(auditSummary?.chargebacks_amount || 0) +
      Number(auditSummary?.refunds_divergence_amount || 0) +
      Number(auditSummary?.status_mismatch_amount || 0) +
      Number(auditSummary?.amount_mismatch_amount || 0) +
      Number(auditSummary?.mp_not_found_amount || 0) +
      Number(auditSummary?.unmatched_mp_amount || 0)
    ) * 100
  ) / 100;

  const rows = [
    {
      key: "index",
      label: t("platformPage.passengerCrossTabIndex"),
      count: indexTickets,
      amount: indexGmv,
      note: t("platformPage.passengerCrossTabIndexHint"),
    },
    {
      key: "xpay_index",
      label: t("platformPage.passengerCrossTabXpayIndex"),
      count: xpayIndexTickets,
      amount: xpayIndexGmv,
      note: t("platformPage.passengerCrossTabXpayIndexHint"),
      deltaCount: xpayIndexTickets - indexTickets,
      deltaAmount: xpayIndexGmv - indexGmv,
    },
    {
      key: "xpay_audit",
      label: t("platformPage.passengerCrossTabXpayAudit"),
      count: localTickets,
      amount: localGmv,
      note: t("platformPage.passengerCrossTabXpayAuditHint"),
      deltaCount: localTickets - xpayIndexTickets,
      deltaAmount: localGmv - xpayIndexGmv,
    },
    {
      key: "mp",
      label: t("platformPage.passengerCrossTabMp"),
      count: mpTickets,
      amount: mpGmv,
      note: t("platformPage.passengerCrossTabMpHint"),
      deltaCount: mpTickets - localTickets,
      deltaAmount: mpGmv - localGmv,
    },
    {
      key: "matched",
      label: t("platformPage.passengerMpAuditMatched"),
      count: matchedTickets,
      amount: matchedGmv,
      note: t("platformPage.passengerCrossTabMatchedHint"),
      deltaCount: matchedTickets - mpTickets,
      deltaAmount: matchedGmv - mpGmv,
    },
    {
      key: "issues",
      label: t("platformPage.passengerCrossTabIssues"),
      count: issueTickets,
      amount: issueGmv,
      note: t("platformPage.passengerCrossTabIssuesHint"),
      emphasize: issueTickets > 0,
    },
  ];

  const fmtDelta = (n, isMoney = false) => {
    const v = Number(n);
    if (!Number.isFinite(v) || v === 0) return "—";
    const prefix = v > 0 ? "+" : "";
    const text = isMoney ? fmtMoney(v) : fmtCount(v);
    return `${prefix}${text}`;
  };

  return (
    <Box sx={{ mb: 2, p: 1.5, borderRadius: 1, bgcolor: (theme) => alpha(theme.palette.info.main, 0.06), border: 1, borderColor: "divider" }}>
      <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5, fontWeight: 600 }}>
        {t("platformPage.passengerCrossTabCompareTitle")}
        {periodLabel ? ` · ${periodLabel}` : ""}
      </Typography>
      <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
        {t("platformPage.passengerCrossTabCompareHint")}
      </Typography>
      <Stack spacing={0.75}>
        {rows.map((row) => (
          <Stack
            key={row.key}
            direction={{ xs: "column", sm: "row" }}
            spacing={{ xs: 0.25, sm: 1 }}
            alignItems={{ sm: "center" }}
            sx={{
              py: 0.5,
              borderBottom: 1,
              borderColor: "divider",
              "&:last-child": { borderBottom: 0 },
              ...(row.emphasize ? { bgcolor: (theme) => alpha(theme.palette.warning.main, 0.08), borderRadius: 0.5, px: 0.5 } : {}),
            }}
          >
            <Typography variant="body2" fontWeight={600} sx={{ minWidth: { sm: 168 }, flexShrink: 0 }}>
              {row.label}
            </Typography>
            <Chip size="small" variant="outlined" label={`${fmtCount(row.count)} · ${fmtMoney(row.amount)}`} />
            {row.deltaCount != null ? (
              <Typography variant="caption" color="text.secondary" sx={{ minWidth: { sm: 88 } }}>
                Δ {fmtDelta(row.deltaCount)} / {fmtDelta(row.deltaAmount, true)}
              </Typography>
            ) : null}
            <Typography variant="caption" color="text.secondary" sx={{ flex: 1 }}>
              {row.note}
            </Typography>
          </Stack>
        ))}
      </Stack>
    </Box>
  );
}

function MpAuditSummaryBar({ summary, audit, globalMode, t, onChipClick, selectedStatKey, from, to }) {
  if (!summary) return null;

  const periodFrom = audit?.period?.from || from;
  const periodTo = audit?.period?.to || to;
  const periodLabel =
    periodFrom && periodTo ? formatProductUsagePeriodBR(periodFrom, periodTo) : null;

  const renderChip = (item) => {
    const clickable = Boolean(onChipClick && item.statKey && Number(item.value) > 0);
    const selected = selectedStatKey === item.statKey;
    const amount =
      item.amount ??
      (item.statKey ? summary[SUMMARY_STAT_AMOUNT_FIELD[item.statKey]] : null) ??
      (item.amountKey ? summary[item.amountKey] : null);
    return (
      <Chip
        key={item.statKey || item.label}
        size="small"
        color={item.color}
        variant={selected ? "filled" : item.color === "primary" ? "outlined" : "filled"}
        label={fmtSummaryChipLabel(item.label, item.value, amount)}
        onClick={
          clickable
            ? () => onChipClick({ statKey: item.statKey, statLabel: item.label, scope: "global" })
            : undefined
        }
        sx={{
          height: "auto",
          py: 0.35,
          "& .MuiChip-label": { whiteSpace: "normal", lineHeight: 1.25 },
          ...(clickable
            ? {
                cursor: "pointer",
                ...(selected ? { boxShadow: 2, outline: "2px solid", outlineColor: "primary.main" } : {}),
              }
            : undefined),
        }}
      />
    );
  };

  const primary = [
    globalMode && audit?.mp_payments_in_range != null
      ? {
          label: t("platformPage.passengerMpAuditMpPayments"),
          value: audit.mp_payments_in_range,
          amount: audit.mp_payments_amount,
          color: "primary",
        }
      : null,
    {
      label: t("platformPage.passengerMpAuditStatLocal"),
      value: summary.local_xpay_count,
      amount: summary.local_xpay_amount,
      color: "default",
      statKey: "local",
    },
    {
      label: t("platformPage.passengerMpAuditMatched"),
      value: summary.matched_count,
      amount: summary.matched_amount,
      color: "success",
      statKey: "matched",
    },
    summary.synced_count > 0
      ? { label: t("platformPage.passengerAuditStep_sync"), value: summary.synced_count, color: "info" }
      : null,
    summary.payment_intent_enriched_count > 0
      ? {
          label: t("platformPage.passengerAuditStep_payment_intent"),
          value: summary.payment_intent_enriched_count,
          color: "secondary",
        }
      : null,
  ].filter(Boolean);

  const issues = [
    {
      label: t("platformPage.passengerMpAuditChargebacks"),
      value: summary.chargebacks_count,
      amount: summary.chargebacks_amount,
      color: "error",
      statKey: "chargebacks_count",
    },
    {
      label: t("platformPage.passengerMpAuditRefundsDivergence"),
      value: summary.refunds_divergence_count,
      amount: summary.refunds_divergence_amount,
      color: "warning",
      statKey: "refunds_divergence_count",
    },
    {
      label: t("platformPage.passengerMpAuditStatusMismatch"),
      value: summary.status_mismatch_count,
      amount: summary.status_mismatch_amount,
      color: "warning",
      statKey: "status_mismatch_count",
    },
    {
      label: t("platformPage.passengerMpAuditAmountMismatch"),
      value: summary.amount_mismatch_count,
      amount: summary.amount_mismatch_amount,
      color: "warning",
      statKey: "amount_mismatch_count",
    },
    {
      label: t("platformPage.passengerMpAuditIssue_mp_not_found"),
      value: summary.mp_not_found_count,
      amount: summary.mp_not_found_amount,
      color: "warning",
      statKey: "mp_not_found_count",
    },
    {
      label: t("platformPage.passengerMpAuditUnmatchedMp"),
      value: summary.unmatched_mp_count,
      amount: summary.unmatched_mp_amount,
      color: "default",
      statKey: "unmatched_mp_count",
    },
  ].filter((item) => Number(item.value) > 0);

  const info = [
    {
      label: t("platformPage.passengerMpAuditRefundsOk"),
      value: summary.refunds_ok_count,
      amount: summary.refunds_ok_amount,
      statKey: "refunds_ok_count",
      color: "success",
    },
    {
      label: t("platformPage.passengerMpAuditIssue_no_mp_id"),
      value: summary.no_mp_id_count,
      amount: summary.no_mp_id_amount,
      statKey: "no_mp_id",
    },
    {
      label: t("platformPage.passengerMpAuditUnmatchedLocal"),
      value: summary.unmatched_local_count,
      amount: summary.unmatched_local_amount,
      statKey: "local",
    },
  ].filter((item) => Number(item.value) > 0);

  return (
    <Box sx={{ mb: 2 }}>
      <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
        {t("platformPage.passengerMpAuditSummaryTitle")}
        {periodLabel ? ` · ${periodLabel}` : ""}
        {onChipClick ? ` · ${t("platformPage.passengerMpAuditChipClickHint")}` : ""}
      </Typography>
      <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
        {t("platformPage.passengerMpAuditSummaryDateHint")}
      </Typography>
      <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap sx={{ mb: issues.length || info.length ? 1 : 0 }}>
        {primary.map(renderChip)}
      </Stack>
      {issues.length ? (
        <>
          <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
            {t("platformPage.passengerMpAuditSummaryIssues")}
          </Typography>
          <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap sx={{ mb: info.length ? 1 : 0 }}>
            {issues.map(renderChip)}
          </Stack>
        </>
      ) : null}
      {info.length ? (
        <>
          <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
            {t("platformPage.passengerMpAuditSummaryInfo")}
          </Typography>
          <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
            {info.map(renderChip)}
          </Stack>
        </>
      ) : null}
    </Box>
  );
}

function buildTenantItems(tenantsPreview = []) {
  return tenantsPreview.map((row) => ({
    id: `tenant-${row.dominioId}`,
    label: row.dominio,
    dominioId: row.dominioId,
    dominio: row.dominio,
    status: "pending",
    detail: null,
  }));
}

function tenantProgressStatus(row, apply = false) {
  if (!row?.ok) {
    if (row?.code === "timeout" || row?.code === "sync_timeout" || row?.code === "sync_deadline") {
      return "warning";
    }
    if (row?.code === "table_missing") return "skipped";
    return "error";
  }
  if (apply && (row.sync?.hasMore || row.sync?.code === "sync_deadline")) return "warning";
  if (countAuditIssues(row.summary) > 0) return "warning";
  return "ok";
}

function tenantProgressDetail(row, t, _stepLabels = {}, _apply = false) {
  if (!row?.ok) {
    if (row?.code === "timeout") return row?.message || t("platformPage.passengerMpAuditTenantTimeout");
    if (row?.code === "sync_timeout" || row?.code === "sync_deadline") {
      return t("platformPage.passengerMpAuditTenantSyncPartial", {
        updated: row?.sync?.updated || 0,
        remaining: row?.sync?.remaining || 0,
      });
    }
    if (row?.code === "tenant_audit_cache_missing") {
      return row?.message || t("platformPage.passengerMpAuditTenantCacheMiss");
    }
    return row?.message || row?.code || t("platformPage.passengerMpAuditError");
  }
  return null;
}

function mapTenantRowToItem(baseItem, row, apply, t, stepLabels = {}) {
  return {
    id: `tenant-${row.dominioId}`,
    label: row.dominio,
    dominioId: row.dominioId,
    dominio: row.dominio,
    status: tenantProgressStatus(row, apply),
    startedAt: baseItem?.startedAt || Date.now(),
    durationMs: row.duration_ms,
    detail: tenantProgressDetail(row, t, stepLabels, apply),
    tenantData: row,
    stats: buildTenantDisplayStats(row, t, apply),
  };
}

function markTenantRunning(items, dominioId) {
  return items.map((it) =>
    it.dominioId === dominioId
      ? { ...it, status: "running", startedAt: it.startedAt || Date.now() }
      : it
  );
}

function upsertTenantItem(items, mapped) {
  const idx = items.findIndex((it) => it.id === mapped.id);
  if (idx >= 0) {
    const next = [...items];
    next[idx] = mapped;
    return next;
  }
  return [...items, mapped];
}

function tenantNeedsSyncApiCall(row) {
  if (!row?.ok) return false;
  const matched = Number(row.summary?.matched_count || 0);
  if (matched <= 0) return false;
  const syncCandidates = Number(row.sync?.candidates);
  if (Number.isFinite(syncCandidates)) return syncCandidates > 0;
  return (
    Number(row.summary?.status_mismatch_count || 0) > 0 ||
    Number(row.summary?.chargebacks_count || 0) > 0 ||
    Number(row.summary?.refunds_divergence_count || 0) > 0
  );
}

function buildLocalSyncSkipResult(tenantRow) {
  return {
    ...tenantRow,
    sync: {
      ok: true,
      updated: 0,
      errors: 0,
      candidates: 0,
      hasMore: false,
      remaining: 0,
      skipped: true,
    },
  };
}

export default function PassengerMercadoPagoAuditPanel({
  dominioId,
  from,
  to,
  salesTotals,
  salesPeriod,
  onSyncComplete,
}) {
  const { t } = useTranslation("app");
  const [audit, setAudit] = useState(null);
  const [loading, setLoading] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [error, setError] = useState("");
  const [syncMsg, setSyncMsg] = useState("");
  const [syncMsgSeverity, setSyncMsgSeverity] = useState("success");
  const [progress, setProgress] = useState(null);
  const [detailSelection, setDetailSelection] = useState(null);
  const globalMode = !dominioId;

  const rangeParams = useMemo(
    () => ({
      from: from || undefined,
      to: to || undefined,
      dominioId: dominioId || undefined,
    }),
    [from, to, dominioId]
  );

  const stepLabels = useMemo(
    () => ({
      mp_search: t("platformPage.passengerAuditStep_mp_search"),
      load_local: t("platformPage.passengerAuditStep_load_local"),
      compare: t("platformPage.passengerAuditStep_compare"),
      reconcile: t("platformPage.passengerAuditStep_reconcile"),
      sync: t("platformPage.passengerAuditStep_sync"),
      payment_intent_enrich: t("platformPage.passengerAuditStep_payment_intent"),
    }),
    [t]
  );

  const fetchMpPage = useCallback(
    async (mpOffset) => {
      if (dominioId) {
        return getPlatformDominioPassengerMercadoPagoAuditMpPage(dominioId, { ...rangeParams, mpOffset });
      }
      return getPlatformPassengerMercadoPagoAuditMpPage({ ...rangeParams, mpOffset });
    },
    [dominioId, rangeParams]
  );

  const runBatchedOperation = useCallback(
    async ({ apply = false } = {}) => {
      const startedAt = Date.now();
      const setRunning = apply ? setSyncing : setLoading;
      setRunning(true);
      setError("");
      setSyncMsg("");
      setSyncMsgSeverity("success");
      setAudit(null);

      const mpItem = {
        id: "mp_search",
        label: stepLabels.mp_search,
        status: "running",
        startedAt,
        detail: t("platformPage.passengerMpAuditActivityMpStart"),
      };

      setProgress({
        startedAt,
        activity: t("platformPage.passengerMpAuditActivityMpStart"),
        items: [mpItem],
      });

      try {
        let mpPayments = [];
        let mpOffset = 0;
        let mpTotal = 0;
        let mpSearchOk = true;
        let mpSearchError = null;
        let period = null;

        for (let page = 0; page < MP_MAX_PAGES; page += 1) {
          const pageData = await fetchMpPage(mpOffset);
          if (!pageData?.ok && pageData?.code === "no_platform_mp") {
            setAudit(pageData);
            setProgress(null);
            return;
          }
          if (!pageData?.ok) {
            mpSearchOk = false;
            mpSearchError = pageData?.mp_search_error || pageData?.message;
            break;
          }

          period = pageData.period || period;
          mpTotal = Number(pageData.mpTotal || 0);
          mpPayments = mpPayments.concat(pageData.payments || []);
          mpOffset = Number(pageData.nextMpOffset || mpPayments.length);
          mpSearchOk = pageData.mp_search_ok !== false;
          mpSearchError = pageData.mp_search_error || null;

          const mpDetail = t("platformPage.passengerMpAuditActivityMpPage", {
            fetched: mpPayments.length,
            total: mpTotal || mpPayments.length,
          });

          setProgress((prev) => ({
            ...prev,
            activity: mpDetail,
            items: [
              {
                ...mpItem,
                detail: mpDetail,
              },
            ],
          }));

          if (!pageData.mpHasMore) break;
        }

        const mpDoneItem = {
          id: "mp_search",
          label: stepLabels.mp_search,
          status: mpSearchOk ? "ok" : "error",
          startedAt,
          durationMs: Date.now() - startedAt,
          stats: mpSearchOk ? buildMpSearchStats(mpPayments.length, t) : undefined,
          detail: mpSearchOk ? null : mpSearchError || t("platformPage.passengerMpAuditSearchError"),
        };

        if (!mpSearchOk) {
          setProgress({ startedAt, activity: mpSearchError || t("platformPage.passengerMpAuditSearchError"), items: [mpDoneItem] });
          setAudit({
            ok: false,
            mp_search_ok: false,
            mp_search_error: mpSearchError,
            period,
            summary: emptySummary(),
          });
          return;
        }

        if (globalMode) {
          let tenantOffset = 0;
          let allTenants = [];
          let mergedSummary = emptySummary();
          let tenantItems = [];
          let tenantsPreview = null;
          let mpCacheKey = null;

          setProgress({
            startedAt,
            activity: t("platformPage.passengerMpAuditActivityReconcileStart"),
            items: [mpDoneItem],
          });

          const finalizeGlobalAudit = (partial = false) => {
            setAudit({
              ok: !partial,
              period,
              mp_search_ok: true,
              mp_payments_in_range: mpPayments.length,
              mp_payments_amount:
                Math.round(
                  mpPayments.reduce((sum, p) => sum + Number(p?.transaction_amount || 0), 0) * 100
                ) / 100,
              summary: mergedSummary,
              tenants: allTenants,
              applied: apply,
              partial,
            });
            setProgress({
              startedAt,
              activity: partial
                ? t("platformPage.passengerMpAuditActivityPartial")
                : t("platformPage.passengerMpAuditActivityDone"),
              items: [mpDoneItem, ...tenantItems],
            });
            if (apply) {
              const synced = mergedSummary.synced_count || 0;
              const paymentIntentEnriched = mergedSummary.payment_intent_enriched_count || 0;
              const hadErrors = Number(mergedSummary.error || 0) > 0 || partial;
              if (hadErrors && synced === 0 && paymentIntentEnriched === 0) {
                setSyncMsg("");
                setSyncMsgSeverity("success");
              } else if ((synced > 0 || paymentIntentEnriched > 0) && (hadErrors || partial)) {
                setSyncMsgSeverity("warning");
                setSyncMsg(
                  buildSyncSuccessMessage(t, { synced, paymentIntentEnriched })
                );
              } else {
                setSyncMsgSeverity("success");
                setSyncMsg(buildSyncSuccessMessage(t, { synced, paymentIntentEnriched }));
              }
              onSyncComplete?.();
            }
          };

          const heartbeatRef = { id: null };
          const stopHeartbeat = () => {
            if (heartbeatRef.id) {
              window.clearInterval(heartbeatRef.id);
              heartbeatRef.id = null;
            }
          };
          const startHeartbeat = (requestKind = "audit", extra = "") => {
            stopHeartbeat();
            heartbeatRef.id = window.setInterval(() => {
              setProgress((prev) => {
                if (!prev) return prev;
                const running = prev.items.find((it) => it.status === "running");
                if (!running) return prev;
                const seconds = Math.max(1, Math.round((Date.now() - (running.startedAt || prev.startedAt)) / 1000));
                const phase = inferWaitingPhase(Date.now() - (running.startedAt || prev.startedAt), requestKind);
                const detail = buildMpAuditPhaseDetail(t, phase, seconds, extra);
                return {
                  ...prev,
                  activity: running.label
                    ? t("platformPage.passengerMpAuditActivityTenantPhase", {
                        dominio: running.label,
                        phase: detail,
                      })
                    : detail,
                  items: prev.items.map((it) => (it.status === "running" ? { ...it, detail } : it)),
                };
              });
            }, 500);
          };

          const handleTenantBatchError = (err, requestKind = "audit", activeDominioId = null) => {
            const failMsg =
              err?.code === "ECONNABORTED"
                ? t("platformPage.passengerMpAuditTenantTimeoutPhase", {
                    phase:
                      requestKind === "sync"
                        ? t("platformPage.passengerAuditStep_sync")
                        : t("platformPage.passengerAuditStep_load_local"),
                  })
                : err?.response?.data?.message || err?.message || t("platformPage.passengerMpAuditError");
            const now = Date.now();
            if (activeDominioId) {
              const idx = tenantItems.findIndex((it) => it.dominioId === activeDominioId);
              const base = idx >= 0 ? tenantItems[idx] : null;
              tenantItems = upsertTenantItem(tenantItems, {
                id: `tenant-${activeDominioId}`,
                label: base?.label || base?.dominio || String(activeDominioId),
                dominioId: activeDominioId,
                dominio: base?.dominio,
                status: "error",
                startedAt: base?.startedAt || now,
                durationMs: base?.startedAt ? now - base.startedAt : null,
                detail: failMsg,
              });
            } else {
              tenantItems = tenantItems.map((it) =>
                it.status === "running"
                  ? { ...it, status: "error", durationMs: it.startedAt ? now - it.startedAt : null, detail: failMsg }
                  : it
              );
            }
            setProgress({
              startedAt,
              activity: failMsg,
              items: [mpDoneItem, ...tenantItems],
            });
            tenantOffset += 1;
            if (tenantOffset >= (tenantsPreview?.length || tenantOffset)) {
              setError(failMsg);
              finalizeGlobalAudit(true);
              return true;
            }
            return false;
          };

          while (true) {
            if (tenantsPreview?.length && tenantOffset >= tenantsPreview.length) {
              stopHeartbeat();
              finalizeGlobalAudit(false);
              return;
            }

            const currentTenant = tenantsPreview?.[tenantOffset];
            if (currentTenant) {
              tenantItems = markTenantRunning(tenantItems, currentTenant.dominioId);
            }

            const runningTenant = tenantItems.find((it) => it.status === "running");
            const tenantsDone = tenantItems.filter((it) =>
              ["ok", "error", "skipped", "warning"].includes(it.status)
            ).length;
            const batchActivity = runningTenant
              ? t("platformPage.passengerMpAuditActivityTenantPhase", {
                  dominio: runningTenant.dominio || runningTenant.label,
                  phase: runningTenant.detail || t("platformPage.passengerMpAuditPhaseAuditRequest"),
                })
              : t("platformPage.passengerMpAuditActivityReconcileBatch", {
                  done: tenantsDone,
                  total: tenantsPreview?.length || tenantItems.length || "…",
                });

            setProgress({
              startedAt,
              activity: batchActivity,
              items: [mpDoneItem, ...tenantItems],
            });

            let batch;
            let tenantRow = null;
            try {
              startHeartbeat("audit", t("platformPage.passengerMpAuditPhaseAuditRequest"));
              batch = await postPlatformPassengerMercadoPagoAuditBatch(rangeParams, {
                ...(tenantOffset === 0 ? { mpPayments } : {}),
                mpCacheKey: mpCacheKey || undefined,
                tenantOffset,
                tenantLimit: 1,
                tenantPhase: "audit",
                apply: false,
              });
              stopHeartbeat();

              if (batch?.code === "mp_cache_missing") {
                stopHeartbeat();
                setError(batch.message || t("platformPage.passengerMpAuditCacheExpired"));
                finalizeGlobalAudit(true);
                return;
              }

              if (batch?.mp_cache_key) mpCacheKey = batch.mp_cache_key;

              if (!tenantsPreview?.length && batch?.tenantsPreview?.length) {
                tenantsPreview = batch.tenantsPreview;
                tenantItems = buildTenantItems(tenantsPreview);
                if (tenantsPreview[0]) {
                  tenantItems = markTenantRunning(tenantItems, tenantsPreview[0].dominioId);
                }
              }

              tenantRow = batch?.tenants?.[0] || null;

              if (apply && tenantRow && tenantRow.ok !== false) {
                if (!tenantNeedsSyncApiCall(tenantRow)) {
                  tenantRow = buildLocalSyncSkipResult(tenantRow);
                } else {
                  tenantItems = markTenantRunning(tenantItems, tenantRow.dominioId);
                  setProgress({
                    startedAt,
                    activity: t("platformPage.passengerMpAuditActivityTenantPhase", {
                      dominio: tenantRow.dominio,
                      phase: t("platformPage.passengerMpAuditPhaseSync"),
                    }),
                    items: [mpDoneItem, ...tenantItems],
                  });

                  let syncOffset = 0;
                  let totalSynced = 0;
                  let syncBatch = null;
                  for (let syncPage = 0; syncPage < 500; syncPage += 1) {
                    const syncBatchNum = Math.floor(syncOffset / 100) + 1;
                    startHeartbeat(
                      "sync",
                      t("platformPage.passengerMpAuditPhaseSyncBatch", { batch: syncBatchNum })
                    );
                    syncBatch = await postPlatformPassengerMercadoPagoAuditBatch(rangeParams, {
                      mpCacheKey: mpCacheKey || undefined,
                      tenantOffset,
                      tenantLimit: 1,
                      tenantPhase: "sync",
                      syncOffset,
                      apply: true,
                    });
                    stopHeartbeat();

                    if (syncBatch?.code === "mp_cache_missing") {
                      stopHeartbeat();
                      setError(syncBatch.message || t("platformPage.passengerMpAuditCacheExpired"));
                      finalizeGlobalAudit(true);
                      return;
                    }

                    const syncRow = syncBatch?.tenants?.[0];
                    if (syncRow) {
                      tenantRow = mergeTenantRowFromBatch(tenantRow, syncRow);
                      totalSynced += Number(syncRow.sync?.updated || 0);
                      const idx = tenantItems.findIndex((it) => it.dominioId === tenantRow.dominioId);
                      tenantItems = upsertTenantItem(
                        tenantItems,
                        mapTenantRowToItem(tenantItems[idx], tenantRow, true, t, stepLabels)
                      );
                    }

                    const hasMoreSync = Boolean(syncRow?.sync?.hasMore && syncRow?.sync?.nextSyncOffset != null);
                    if (hasMoreSync) {
                      syncOffset = Number(syncRow.sync.nextSyncOffset);
                      tenantItems = markTenantRunning(tenantItems, tenantRow.dominioId);
                      const partialDetail = t("platformPage.passengerMpAuditPhaseSyncPartial", {
                        synced: totalSynced,
                        remaining: syncRow.sync?.remaining || 0,
                        batch: syncBatchNum + 1,
                      });
                      tenantItems = tenantItems.map((it) =>
                        it.dominioId === tenantRow.dominioId ? { ...it, detail: partialDetail } : it
                      );
                      setProgress({
                        startedAt,
                        activity: t("platformPage.passengerMpAuditActivityTenantPhase", {
                          dominio: tenantRow.dominio,
                          phase: partialDetail,
                        }),
                        items: [mpDoneItem, ...tenantItems],
                      });
                      continue;
                    }
                    break;
                  }

                  batch = {
                    ...batch,
                    tenants: [tenantRow],
                    summary: syncBatch?.summary || batch?.summary,
                  };
                }
              }
            } catch (err) {
              stopHeartbeat();
              if (handleTenantBatchError(err, tenantRow ? "sync" : "audit", tenantRow?.dominioId)) return;
              continue;
            }

            for (const row of batch?.tenants || []) {
              const idx = tenantItems.findIndex((it) => it.dominioId === row.dominioId);
              tenantItems = upsertTenantItem(
                tenantItems,
                mapTenantRowToItem(tenantItems[idx], row, apply, t, stepLabels)
              );
            }

            allTenants = allTenants.concat(batch?.tenants || []);
            mergedSummary = mergeSummary(mergedSummary, batch?.summary);
            period = batch?.period || period;

            setProgress({
              startedAt,
              activity: t("platformPage.passengerMpAuditActivityReconcileBatch", {
                done: tenantItems.filter((it) => ["ok", "error", "skipped", "warning"].includes(it.status)).length,
                total: batch?.tenantsTotal || tenantsPreview?.length || tenantItems.length,
              }),
              items: [mpDoneItem, ...tenantItems],
            });

            if (!batch?.hasMore || tenantOffset >= (batch?.tenantsTotal || tenantsPreview?.length || 0)) {
              stopHeartbeat();
              finalizeGlobalAudit(false);
              return;
            }

            tenantOffset = Number(batch?.nextTenantOffset ?? tenantOffset + 1);
          }
        }

        if (!dominioId) {
          setError(t("platformPage.passengerMpAuditNoDominio"));
          return;
        }

        setProgress({
          startedAt,
          activity: t("platformPage.passengerMpAuditActivityLocalCompare"),
          items: [
            mpDoneItem,
            {
              id: `tenant-${dominioId}`,
              label: t("platformPage.passengerMpAuditActivityLocalCompare"),
              status: "running",
              startedAt: Date.now(),
              detail: null,
            },
          ],
        });

        const data = await getPlatformDominioPassengerReconciliation(
          dominioId,
          rangeParams,
          { mpPayments, apply }
        );

        const tenantLine = {
          id: `tenant-${dominioId}`,
          dominioId: Number(dominioId),
          dominio: data?.dominio || String(dominioId),
          label: data?.dominio || String(dominioId),
          status: tenantProgressStatus(data, apply),
          durationMs: data?.duration_ms,
          detail: tenantProgressDetail(data, t, stepLabels, apply),
          tenantData: data,
          stats: buildTenantDisplayStats(data, t, apply),
        };
        setProgress({
          startedAt,
          activity: t("platformPage.passengerMpAuditActivityDone"),
          items: [mpDoneItem, tenantLine],
        });
        setAudit({
          ...data,
          dominioId: Number(dominioId),
          dominio: data?.dominio || tenantLine.dominio,
          schema: data?.schema || resolveTenantDatabaseName(data?.dominio || tenantLine.dominio, null),
        });
        if (apply) {
          setSyncMsg(
            buildSyncSuccessMessage(t, {
              synced: data?.sync?.updated || 0,
              paymentIntentEnriched: resolvePaymentIntentEnriched(data),
            })
          );
          onSyncComplete?.();
        }
      } catch (err) {
        setAudit(null);
        setError(err?.response?.data?.message || err?.message || t("platformPage.passengerMpAuditError"));
        setProgress(null);
      } finally {
        setRunning(false);
      }
    },
    [dominioId, fetchMpPage, globalMode, onSyncComplete, rangeParams, stepLabels, t]
  );

  const runAudit = useCallback(() => {
    setDetailSelection(null);
    return runBatchedOperation({ apply: false });
  }, [runBatchedOperation]);
  const runSync = useCallback(() => {
    setDetailSelection(null);
    return runBatchedOperation({ apply: true });
  }, [runBatchedOperation]);

  const handleTenantRowStatClick = useCallback((row, stat) => {
    if (!row?.dominioId || !stat?.key) return;
    const stepId = `tenant-${row.dominioId}`;
    setDetailSelection((prev) =>
      prev?.scope === "tenant" &&
      prev?.stepId === stepId &&
      prev?.statKey === stat.key
        ? null
        : {
            scope: "tenant",
            stepId,
            dominioId: row.dominioId,
            dominio: row.dominio,
            statKey: stat.key,
            statLabel: stat.label,
          }
    );
  }, []);

  const handleSummaryChipClick = useCallback(
    (payload) => {
      if (!payload?.statKey) return;
      const scope = globalMode ? "global" : "tenant";
      setDetailSelection((prev) =>
        prev?.scope === scope && prev?.statKey === payload.statKey
          ? null
          : {
              ...payload,
              scope,
              dominioId: globalMode ? null : Number(dominioId) || null,
              dominio: globalMode ? null : audit?.dominio,
            }
      );
    },
    [globalMode, dominioId, audit?.dominio]
  );

  const selectedTenantStat = useMemo(() => {
    if (detailSelection?.scope !== "tenant") return null;
    return { dominioId: detailSelection.dominioId, statKey: detailSelection.statKey };
  }, [detailSelection]);

  const {
    sortBy,
    sortOrder,
    handleSort,
    sortRows,
  } = useAnalyticsTableSort("passenger-mp-audit", TENANT_SORT_FIELDS, { sortBy: "workspace", sortOrder: "asc" });

  const tenantRows = useMemo(
    () => sortRows(buildTenantResultRows(audit, progress)),
    [audit, progress, sortRows]
  );

  const showTenantResultsTable = globalMode && tenantRows.length > 0;

  const summary = audit?.summary;
  const tenantAudit = dominioId ? audit : null;
  const operationRunning = loading || syncing;

  const issueItems = useMemo(() => {
    if (!tenantAudit) return [];
    const items = [];
    for (const row of tenantAudit.chargebacks || []) {
      items.push({
        _rowKey: `cb-${row.venda?.id}`,
        type: "chargeback",
        mp_payment_id: row.venda?.mp_payment_id,
        localizador: row.venda?.localizador,
        passenger: row.venda?.nome_passageiro,
        mp_status: row.mp_status,
        local_status: row.venda?.status_venda,
      });
    }
    for (const row of tenantAudit.refunds || []) {
      const syncState = row?.sync_state || row?.refundAssess?.sync_state;
      items.push({
        _rowKey: `rf-${row.venda?.id}-${syncState || "x"}`,
        type: syncState === "ok" ? "refund_ok" : "refund_divergence",
        mp_payment_id: row.venda?.mp_payment_id,
        localizador: row.venda?.localizador,
        passenger: row.venda?.nome_passageiro,
        mp_status: row.mp_status,
        local_status: row.venda?.status_venda,
        refund_issues: row?.refundAssess?.issues || [],
      });
    }
    for (const row of tenantAudit.statusMismatch || []) {
      items.push({
        _rowKey: `sm-${row.venda?.id}`,
        type: "status_mismatch",
        mp_payment_id: row.venda?.mp_payment_id,
        localizador: row.venda?.localizador,
        passenger: row.venda?.nome_passageiro,
        mp_status: row.mp_status,
        local_status: row.local_venda,
        expected_status: row.expected_venda,
      });
    }
    for (const row of tenantAudit.unmatchedLocal || []) {
      items.push({
        _rowKey: `ul-${row.venda?.id}`,
        type: row.type || "unmatched_local",
        mp_payment_id: row.venda?.mp_payment_id || row.mp_payment_id,
        localizador: row.venda?.localizador,
        passenger: row.venda?.nome_passageiro,
        mp_status: "—",
        local_status: row.venda?.status_venda,
      });
    }
    return items;
  }, [tenantAudit]);

  const mpConfigured = audit?.code !== "no_platform_mp";

  const progressSummary = useMemo(() => {
    const items = progress?.items || [];
    if (!items.length) return "";
    const done = items.filter((s) => ["ok", "error", "skipped", "warning"].includes(s.status)).length;
    return t("platformPage.passengerAuditProgressSummary", { done, total: items.length });
  }, [progress, t]);

  return (
    <AnalyticsSection
      title={t("platformPage.passengerMpAuditTitle")}
      hint={t("platformPage.passengerMpAuditHint")}
      loading={false}
    >
      <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} alignItems={{ sm: "center" }} sx={{ mb: 2 }}>
        <Tooltip title={t("platformPage.passengerMpAuditRunTooltip")}>
          <span>
            <Button
              size="small"
              variant="outlined"
              startIcon={loading ? <CircularProgress size={16} /> : <AccountBalanceIcon />}
              onClick={runAudit}
              disabled={operationRunning}
            >
              {t("platformPage.passengerMpAuditRun")}
            </Button>
          </span>
        </Tooltip>
        <Tooltip title={t("platformPage.passengerMpAuditSyncTooltip")}>
          <span>
            <Button
              size="small"
              variant="contained"
              color="warning"
              startIcon={syncing ? <CircularProgress size={16} color="inherit" /> : <SyncIcon />}
              onClick={runSync}
              disabled={operationRunning || !mpConfigured}
            >
              {t("platformPage.passengerMpAuditSync")}
            </Button>
          </span>
        </Tooltip>
        <Chip
          size="small"
          color="warning"
          variant="outlined"
          label={t("platformPage.passengerMpAuditEmergencyBadge")}
        />
      </Stack>

      <Alert severity="info" variant="outlined" sx={{ mb: 1.5, py: 0.5 }}>
        {t("platformPage.passengerOpsAutoSyncHint")}
      </Alert>

      {error ? (
        <Alert severity="error" sx={{ mb: 1.5 }} onClose={() => setError("")}>
          {error}
        </Alert>
      ) : null}
      {syncMsg ? (
        <Alert severity={syncMsgSeverity} sx={{ mb: 1.5 }} onClose={() => setSyncMsg("")}>
          {syncMsg}
        </Alert>
      ) : null}
      {audit?.code === "no_platform_mp" ? (
        <Alert severity="info" sx={{ mb: 1.5 }}>
          {audit.message || t("platformPage.passengerMpAuditNoToken")}
        </Alert>
      ) : null}
      {audit?.mp_search_ok === false ? (
        <Alert severity="warning" sx={{ mb: 1.5 }}>
          {t("platformPage.passengerMpAuditSearchError")}
        </Alert>
      ) : null}

      {(operationRunning || progress?.items?.length > 0) ? (
        <ConsoleOperationSteps
          running={operationRunning}
          steps={progress?.items || []}
          summaryLabel={progressSummary}
          activityLabel={progress?.activity || ""}
          operationStartedAt={progress?.startedAt || null}
          hideStepList={globalMode}
        />
      ) : null}

      {showTenantResultsTable ? (
        <Box sx={{ mb: 2 }}>
          <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
            {t("platformPage.passengerMpAuditTenantResultsTitle")}
            {!operationRunning && audit
              ? ` · ${t("platformPage.passengerMpAuditChipClickHint")}`
              : ""}
          </Typography>
          <MpAuditTenantResultsTable
            rows={tenantRows}
            t={t}
            sortBy={sortBy}
            sortOrder={sortOrder}
            onSort={handleSort}
            sortFields={TENANT_SORT_FIELDS}
            onStatClick={!operationRunning && audit ? handleTenantRowStatClick : null}
            selectedStat={selectedTenantStat}
            showSyncColumn={Boolean(audit?.applied || syncing)}
            emptyLabel={t("platformPage.passengerMpAuditEmpty")}
          />
        </Box>
      ) : null}

      {summary ? (
        <>
          <MpAuditCrossTabComparison
            salesTotals={salesTotals}
            salesPeriod={salesPeriod}
            auditSummary={summary}
            audit={audit}
            from={from}
            to={to}
            t={t}
          />
          <MpAuditSummaryBar
            summary={summary}
            audit={audit}
            globalMode={globalMode}
            t={t}
            from={from}
            to={to}
            onChipClick={audit && (globalMode ? audit?.tenants?.length : true) ? handleSummaryChipClick : null}
            selectedStatKey={
              detailSelection && !detailSelection.stepId ? detailSelection.statKey : null
            }
          />
        </>
      ) : null}

      <MpAuditDetailDialog
        selection={detailSelection}
        tenants={audit?.tenants}
        singleTenantAudit={!globalMode ? audit : null}
        t={t}
        onClose={() => setDetailSelection(null)}
      />

      {globalMode && audit && !operationRunning && countAuditIssues(summary) === 0 && !audit?.partial ? (
        <Alert severity="success" sx={{ mb: 2 }}>
          {t("platformPage.passengerMpAuditOk")}
        </Alert>
      ) : null}
      {globalMode && audit && !operationRunning && (countAuditIssues(summary) > 0 || audit?.partial) ? (
        <Alert severity="warning" sx={{ mb: 2 }}>
          {t("platformPage.passengerMpAuditHasIssues")}
        </Alert>
      ) : null}

      {!globalMode && issueItems.length > 0 && !detailSelection ? (
        <Box sx={{ mt: 1 }}>
          <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
            {t("platformPage.passengerMpAuditIssuesTitle")}
          </Typography>
          <AnalyticsDataTable
            rows={issueItems}
            emptyLabel={t("platformPage.passengerMpAuditIssuesEmpty")}
            columns={[
              {
                id: "type",
                label: t("platformPage.passengerMpAuditColType"),
                render: (row) => (
                  <Chip
                    size="small"
                    icon={row.type === "chargeback" ? <GavelIcon /> : undefined}
                    color={
                      row.type === "chargeback"
                        ? "error"
                        : row.type === "refund_divergence"
                          ? "warning"
                          : row.type === "refund_ok"
                            ? "success"
                            : row.type === "refund"
                              ? "warning"
                              : "default"
                    }
                    variant="outlined"
                    label={t(`platformPage.passengerMpAuditIssue_${row.type}`, { defaultValue: row.type })}
                  />
                ),
              },
              {
                id: "mp_payment_id",
                label: "MP ID",
                render: (row) => (
                  <MpPaymentIdCell
                    value={row.mp_payment_id}
                    title={t("platformPage.passengerMpAuditOpenMpPayment", { defaultValue: "Abrir venda no Mercado Pago" })}
                  />
                ),
              },
              { id: "localizador", label: t("passagensPage.colLocator", { defaultValue: "Localizador" }), render: (row) => row.localizador || "—" },
              { id: "passenger", label: t("passagensPage.colClient"), render: (row) => row.passenger || "—" },
              { id: "mp_status", label: t("platformPage.passengerMpAuditColMpStatus"), render: (row) => row.mp_status || "—" },
              {
                id: "local_status",
                label: t("platformPage.passengerMpAuditColLocalStatus"),
                render: (row) => row.expected_status ? `${row.local_status || "—"} → ${row.expected_status}` : row.local_status || "—",
              },
            ]}
          />
        </Box>
      ) : null}

      {!globalMode && audit && issueItems.length === 0 && summary && !detailSelection ? (
        <Alert severity={summary.status === "ok" ? "success" : "warning"}>
          {summary.status === "ok"
            ? t("platformPage.passengerMpAuditOk")
            : t("platformPage.passengerMpAuditHasIssues")}
        </Alert>
      ) : null}
    </AnalyticsSection>
  );
}
